import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError, finalize } from 'rxjs/operators';
import { HttpService } from './http.service';
import { URLUtils } from '../urlUtils';

export interface Note {
    id: string;
    caseId: string;
    content: string;
    createdAt: Date;
    updatedAt?: Date;
    author?: string;
    shared?: boolean;
    // True only when the backend actually gave us a note_id (from the Add/Edit
    // responses, or a future list response that includes one). Edit/Delete
    // are backed by /notes/{note_id}, so this is the one flag that tells the
    // UI whether those actions are safe to offer for a given note — see
    // CaseDetailComponent.openEditNote/openDeleteNote. `id` below is never a
    // substitute for this: when the backend hasn't given us an id, `id` is a
    // locally-generated key that exists only so *ngFor/cache bookkeeping has
    // something stable to key off of, and it is never sent to the backend.
    hasPersistedId?: boolean;
    // True only when the backend actually gave us a created/updated
    // timestamp for this note. Notes without one can't be meaningfully
    // placed in newest-first order (see sortNotesNewestFirst) and are
    // rendered using whatever order the API returned them in.
    hasTimestamp?: boolean;
    // Raw API fields
    note_id?: string;
    note?: string;
    note_details?: string;
    created_at?: string;
    updated_at?: string;
    added_by?: string;
    created_on?: string;
    edited_on?: string | null;
}

// Mirrors the 150-word cap enforced live in NoteDialogComponent
// (clampToWordLimit). Re-applied here as a defense-in-depth check so the
// API contract is protected even if a future caller adds/edits notes
// without going through that dialog.
const MAX_NOTE_WORDS = 150;

function clampToWordLimit(text: string): string {
    const value = (text || '').trim();
    const words = value.split(/\s+/).filter(w => w.length > 0);
    if (words.length <= MAX_NOTE_WORDS) {
        return value;
    }
    return words.slice(0, MAX_NOTE_WORDS).join(' ');
}

export interface ShareNoteResult {
    success: boolean;
}

// API Response Types
interface NoteActionResponse {
    error: boolean;
    msg: string;
    note?: {
        note_id: string;
        pre_read_id: string;
        note: string;
        created_at: string;
        updated_at: string;
        [key: string]: any;
    };
}

/**
 * Case Information — Notes data service (Lawyer app / Relationships /
 * Individual / Case Information / Notes tab only).
 * 
 * Now connected to real backend APIs for Notes CRUD operations.
 */
@Injectable({ providedIn: 'root' })
export class CaseNotesService {

    // Track pending operations to prevent duplicates
    private pendingOperations = new Map<string, boolean>();

    // Cache for notes by caseId to enable optimistic updates
    private notesCache = new Map<string, Note[]>();

    constructor(private httpService: HttpService) { }

    /**
     * Normalize a raw API note object into the application's Note interface.
     *
     * KNOWN BACKEND LIMITATION: the documented list response
     * (GET /v3/prereads/shared/:pre_read_id/notes) only returns
     * `{ note, note_details }` — no note_id, created_at, or updated_at. The
     * Edit/Delete endpoints require note_id. Rather than invent one, we
     * mark such notes with `hasPersistedId: false` and let
     * CaseDetailComponent disable Edit/Delete for them instead of sending a
     * request the backend can't fulfil. If the real list response does
     * include note_id/timestamps (this normalizer reads them defensively
     * from several possible field names), those notes get full Edit/Delete
     * support automatically.
     */
    private normalizeNote(raw: any, caseId: string): Note {
        // Try to find a real backend id from various possible fields. Do NOT
        // fabricate one here — a fabricated id would silently pass the
        // hasPersistedId check below and produce a doomed PATCH/DELETE call.
        const backendId: string | undefined = raw.note_id || raw.id || raw._id || undefined;
        const hasPersistedId = !!backendId;
        // `id` still needs *some* stable local value for cache bookkeeping
        // and *ngFor — this local key is only ever used for that; it is
        // never sent to the backend (see hasPersistedId).
        const id = backendId || this.generateLocalKey();

        // Try to find content from various possible fields
        const content = raw.note || raw.content || raw.note_details || raw.text || '';
        
        // Parse dates - API uses created_on or created_at
        let createdAt: Date;
        let updatedAt: Date | undefined;
        
        const rawCreated = raw.created_on || raw.created_at || raw.createdAt || raw.created;
        const rawUpdated = raw.edited_on || raw.updated_at || raw.updatedAt || raw.updated;
        const hasTimestamp = !!rawCreated;
        
        try {
            createdAt = rawCreated ? new Date(rawCreated) : new Date();
        } catch {
            createdAt = new Date();
        }
        
        try {
            updatedAt = rawUpdated ? new Date(rawUpdated) : undefined;
        } catch {
            updatedAt = undefined;
        }

        return {
            id,
            caseId,
            content,
            createdAt,
            updatedAt,
            author: raw.added_by || raw.author || raw.created_by || 'Unknown',
            shared: raw.shared || false,
            hasPersistedId,
            hasTimestamp,
            // Preserve raw fields for debugging
            note_id: backendId,
            note: raw.note || raw.content,
            note_details: raw.note_details,
            created_at: raw.created_at || raw.createdAt,
            updated_at: raw.updated_at || raw.updatedAt,
            added_by: raw.added_by,
            created_on: raw.created_on,
            edited_on: raw.edited_on
        };
    }

    // Local-only UI key for notes the backend hasn't given a real id for
    // yet. Never sent in any request — see hasPersistedId on Note.
    private generateLocalKey(): string {
        return 'LOCAL' + Date.now().toString(36).toUpperCase() + Math.floor(Math.random() * 1000);
    }

    /**
     * Seeds this case's notes from the Case Information API response.
     * This is called during initial load from the case detail.
     */
    seedNotes(caseId: string, notes: Note[]): void {
        if (this.notesCache.has(caseId) && this.notesCache.get(caseId)!.length > 0) {
            return;
        }
        this.notesCache.set(caseId, notes);
    }

    /**
     * Returns all notes for a case, newest first.
     * GET /v3/prereads/shared/:pre_read_id/notes
     */
    getNotes(caseId: string): Observable<Note[]> {
        const url = URLUtils.getSharedPreReadNotes(caseId);
        
        return this.httpService.getFeaturesdata(url).pipe(
            map((response: any) => {
                let notesData: any[] = [];
                
                // Handle response structure: { data: { notes: [...] } }
                if (response && response.data && response.data.notes && Array.isArray(response.data.notes)) {
                    notesData = response.data.notes;
                } else if (response && response.notes && Array.isArray(response.notes)) {
                    notesData = response.notes;
                } else if (Array.isArray(response)) {
                    notesData = response;
                }

                const normalizedNotes = notesData
                    .filter(n => n !== null && n !== undefined)
                    .map(n => this.normalizeNote(n, caseId))
                    .filter(n => n.content && n.content.trim().length > 0);

                this.notesCache.set(caseId, normalizedNotes);
                
                return this.sortNotesNewestFirst(normalizedNotes);
            }),
            catchError((error) => {
                if (this.notesCache.has(caseId)) {
                    return of(this.sortNotesNewestFirst(this.notesCache.get(caseId)!));
                }
                return throwError(() => error);
            })
        );
    }

    // Sorts newest-first using real timestamps where the backend provided
    // one. Notes without a timestamp (see hasTimestamp) can't be placed
    // meaningfully in the order, so they're kept after the timestamped ones,
    // in the order the API returned them (Array.sort is stable).
    private sortNotesNewestFirst(notes: Note[]): Note[] {
        return [...notes].sort((a, b) => {
            if (a.hasTimestamp && b.hasTimestamp) {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            }
            if (a.hasTimestamp && !b.hasTimestamp) { return -1; }
            if (!a.hasTimestamp && b.hasTimestamp) { return 1; }
            return 0;
        });
    }

    /**
     * Adds one or more notes to a case.
     * POST /v3/prereads/shared/{pre_read_id}/notes
     *
     * CONFIRMED API CONTRACT — a single bulk call, all notes sent together
     * as an array of objects under "notes":
     *   {
     *     "notes": [
     *       { "note": "Follow-up required...", "note_details": "" },
     *       { "note": "Documents require review...", "note_details": "" }
     *     ]
     *   }
     *
     * This replaces the earlier one-request-per-note implementation (which
     * only ever sent a single string and could never have matched this
     * array-of-objects shape). Sorting/caching is still handled the same
     * way as everywhere else, just via a refetch — see the note below.
     *
     * RESPONSE HANDLING: this endpoint's own echoed note data is not used.
     * It has proven unreliable — sometimes it's the case's *entire* current
     * note list rather than just the newly-created ones (duplicating every
     * pre-existing note when merged onto the cache), and sometimes a
     * freshly-added note comes back with no id at all (leaving Edit/Delete
     * disabled for it). Once the POST succeeds, this refetches via
     * getNotes() instead, which is the same authoritative source of truth
     * used everywhere else Note[] gets cached.
     */
    addNotes(caseId: string, contents: string[]): Observable<Note[]> {
        const validContents = contents.map(c => clampToWordLimit(c)).filter(c => c.length > 0);

        if (validContents.length === 0) {
            return of(this.sortNotesNewestFirst(this.notesCache.get(caseId) || []));
        }

        const operationKey = `add-${caseId}`;

        if (this.pendingOperations.get(operationKey)) {
            return throwError(() => new Error('An add operation is already in progress'));
        }

        this.pendingOperations.set(operationKey, true);

        const url = URLUtils.addSharedPreReadNote(caseId);

        const requestBody = {
            notes: validContents.map(content => ({ note: content, note_details: '' }))
        };

        return this.httpService.sendPostRequest(url, requestBody).pipe(
            switchMap((response: any) => {
                console.log('Add notes response:', response);

                if (response && response.error === true) {
                    const errorMsg = response.msg || response.errors?.[0]?.msg || 'Failed to add notes';
                    return throwError(() => new Error(errorMsg));
                }

                // Don't trust the Add endpoint's own echoed note data for
                // ids — it's proven unreliable (sometimes the freshly-added
                // note comes back with no id at all), which is what left
                // Edit/Delete disabled right after an add even though the
                // note was saved fine. Instead of parsing the echo, refetch
                // the authoritative list from GET .../notes — the same
                // request getNotes() already makes — so every note (new and
                // old) ends up with whatever real id state the backend
                // actually has for it.
                return this.getNotes(caseId);
            }),
            finalize(() => this.pendingOperations.delete(operationKey))
        );
    }

    /**
     * Updates a single note's content.
     * PATCH /v3/prereads/shared/{pre_read_id}/notes/{note_id}
     *
     * CONFIRMED (via captured network error): unlike the bulk Add endpoint,
     * which wants { "notes": [ { "note", "note_details" }, ... ] }, this
     * single-note PATCH endpoint validates on a top-level "note" field —
     * the API's own response was { "errors": [ { "field": "note", "msg":
     * "This field is required." } ] } when the body was wrapped in a
     * "notes" array. Fixed by sending the note's fields flat, matching the
     * object shape used inside the bulk array but without the array/
     * "notes" wrapper:
     *   { "note": "updated text", "note_details": "" }
     */
    updateNote(caseId: string, noteId: string, content: string): Observable<Note[]> {
        const trimmedContent = clampToWordLimit(content);
        if (!trimmedContent) {
            return throwError(() => new Error('Note content cannot be empty'));
        }

        const operationKey = `update-${caseId}-${noteId}`;
        
        if (this.pendingOperations.get(operationKey)) {
            return throwError(() => new Error('An update operation is already in progress for this note'));
        }
        
        this.pendingOperations.set(operationKey, true);

        const url = URLUtils.updateSharedPreReadNote(caseId, noteId);

        const requestBody = { note: trimmedContent, note_details: '' };

        return this.httpService.sendPatchRequest(url, requestBody).pipe(
            map((response: any) => {
                if (response && response.error === true) {
                    const errorMsg = response.msg || response.errors?.[0]?.msg || 'Failed to update note';
                    throw new Error(errorMsg);
                }

                const existingNotes = this.notesCache.get(caseId) || [];
                let updatedNote: Note | null = null;

                let updatedRaw: any = null;
                if (Array.isArray(response?.notes) && response.notes.length > 0) {
                    updatedRaw = response.notes[0];
                } else if (Array.isArray(response?.data?.notes) && response.data.notes.length > 0) {
                    updatedRaw = response.data.notes[0];
                } else if (response?.note) {
                    updatedRaw = response.note;
                } else if (response?.data?.note) {
                    updatedRaw = response.data.note;
                }

                if (updatedRaw) {
                    updatedNote = this.normalizeNote(updatedRaw, caseId);
                }

                const updatedNotes = existingNotes.map(n => {
                    if (n.id === noteId || n.note_id === noteId) {
                        if (updatedNote) {
                            return { ...updatedNote, id: noteId };
                        }
                        return { ...n, content: trimmedContent, updatedAt: new Date() };
                    }
                    return n;
                });

                this.notesCache.set(caseId, updatedNotes);
                this.pendingOperations.delete(operationKey);
                
                return this.sortNotesNewestFirst(updatedNotes);
            }),
            catchError((error) => {
                this.pendingOperations.delete(operationKey);
                return throwError(() => error);
            })
        );
    }

    /**
     * Deletes a single note.
     * DELETE /v3/prereads/shared/{pre_read_id}/notes/{note_id}
     */
    deleteNote(caseId: string, noteId: string): Observable<Note[]> {
        const operationKey = `delete-${caseId}-${noteId}`;
        
        if (this.pendingOperations.get(operationKey)) {
            return throwError(() => new Error('A delete operation is already in progress for this note'));
        }
        
        this.pendingOperations.set(operationKey, true);

        const url = URLUtils.deleteSharedPreReadNote(caseId, noteId);

        const existingNotes = this.notesCache.get(caseId) || [];
        const updatedNotes = existingNotes.filter(n => n.id !== noteId && n.note_id !== noteId);
        this.notesCache.set(caseId, updatedNotes);

        return this.httpService.sendDeleteRequest(url).pipe(
            map((response: any) => {
                if (response && response.error === true) {
                    this.notesCache.set(caseId, existingNotes);
                    throw new Error(response.msg || 'Failed to delete note');
                }
                this.pendingOperations.delete(operationKey);
                return this.sortNotesNewestFirst(updatedNotes);
            }),
            catchError((error) => {
                this.notesCache.set(caseId, existingNotes);
                this.pendingOperations.delete(operationKey);
                return throwError(() => error);
            })
        );
    }

    /**
     * Shares a note with a recipient.
     */
    shareNote(caseId: string, noteId: string, recipient: string): Observable<ShareNoteResult> {
        const notes = this.notesCache.get(caseId) || [];
        const note = notes.find(n => n.id === noteId || n.note_id === noteId);
        
        if (!note) {
            return throwError(() => new Error('Note not found'));
        }

        note.shared = true;
        this.notesCache.set(caseId, notes);

        return of({ success: true });
    }

    /**
     * Clear cached notes for a case.
     */
    clearCache(caseId?: string): void {
        if (caseId) {
            this.notesCache.delete(caseId);
        } else {
            this.notesCache.clear();
        }
    }

    /**
     * Check if a note operation is pending for a specific case/note.
     */
    isOperationPending(caseId: string, noteId?: string): boolean {
        const key = noteId ? `update-${caseId}-${noteId}` : `add-${caseId}`;
        return this.pendingOperations.get(key) || false;
    }
}